import { DataList, DataListItem } from '@app/components/DataList'
import LeaseTermDetailsCard from '@app/components/LeaseTermDetailsCard'
import PillListRenderer from '@app/components/PillListRenderer'
import { ListingType } from '@app/models/enums'
import { bindFunctions } from '@app/utils/class'
import {
  formatBoolean,
  formatCurrency,
  formatDate,
  humanize,
} from '@app/utils/formatting'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Link } from 'react-router-dom'
import FeeBonusDistribution from './FeeBonusDistribution'
import { PipelinePageProps } from './PipelinePage'

export default class PipelineDetails extends React.PureComponent<
  PipelinePageProps
> {
  constructor(props: PipelinePageProps) {
    super(props)
    bindFunctions(this)
  }

  public render() {
    const isLease = this.props.pipeline.listing.type === ListingType.Lease

    return (
      <DocumentTitle title={`${this.props.pipeline.name} :: Details`}>
        <div>
          {this._renderGeneralInformation(isLease)}
          {this._renderNotes()}
          {this._renderEarnestMoney(isLease)}
          {this._renderFeeBonusDistribution()}
          {this._renderBuyerInformation(isLease)}
          {this._renderSellerInformation(isLease)}
          {this._renderLeasePriceBreakdown(isLease)}
        </div>
      </DocumentTitle>
    )
  }

  private _renderBuyerInformation(isLease: boolean) {
    return (
      <div>
        <h5 className='section-title'>
          {isLease ? 'Tenant Information' : 'Buyer Information'}
        </h5>
        <div className='mx-3'>
          <DataList columnCount={2}>
            <DataListItem
              label={isLease ? 'Tenant(s)' : 'Buyer(s)'}
              value={
                <PillListRenderer
                  list={this.props.pipeline.buyers}
                  baseNavigateUrl='/connection'
                  idValue='contactId'
                />
              }
            />
            <DataListItem
              label={isLease ? "Tenant's Agent(s)" : 'Selling Agent(s)'}
              value={
                this.props.pipeline.sellingAgents.size === 0 ? (
                  'No Selling Agent'
                ) : (
                  <PillListRenderer
                    list={this.props.pipeline.sellingAgents}
                    baseNavigateUrl='/connection'
                    idValue='contactId'
                  />
                )
              }
            />
            <DataListItem
              label={"Buyer's Lender"}
              value={this.props.pipeline.buyerLender}
              visible={!isLease}
            />
            <DataListItem
              label={'Title Company'}
              value={
                this.props.pipeline.buyersTitleCompany
                  ? this.props.pipeline.buyersTitleCompany.name
                  : ''
              }
              visible={!isLease}
            />
          </DataList>
        </div>
      </div>
    )
  }

  private _renderEarnestMoney(isLease: boolean) {
    if (isLease) {
      return null
    }

    return (
      <div>
        <h5 className='section-title'>Earnest Money</h5>
        <div className='mx-3'>
          {this.props.pipeline.earnestMoney <= 0 && (
            <div>No earnest money is being held</div>
          )}
          {this.props.pipeline.earnestMoney > 0 && (
            <DataList columnCount={2}>
              <DataListItem
                label='Deposit Date'
                value={formatDate(
                  this.props.pipeline.earnestMoneyDepositDate,
                  'MM/DD/YYYY',
                )}
              />
              <DataListItem
                label='Amount'
                value={formatCurrency(this.props.pipeline.earnestMoney, 2)}
              />
              <DataListItem
                label='Holding Location'
                value={this.props.pipeline.earnestMoneyLocation}
              />
              <DataListItem
                label='Withdrawl Date'
                value={formatDate(
                  this.props.pipeline.earnestMoneyWithdrawlDate,
                  'MM/DD/YYYY',
                )}
              />
            </DataList>
          )}
        </div>
      </div>
    )
  }

  private _renderFeeBonusDistribution() {
    return (
      <div>
        <h5 className='section-title'>Fee / Bonus Distribution</h5>
        <div className='mx-3'>
          <FeeBonusDistribution
            pipeline={this.props.pipeline}
            siteSettings={this.props.siteSettings}
            user={this.props.user}
          />
        </div>
      </div>
    )
  }

  private _renderGeneralInformation(isLease: boolean) {
    return (
      <div>
        <h5 className='section-title'>General Information</h5>
        <div className='mx-3'>
          <DataList columnCount={2}>
            <DataListItem
              label='Status'
              value={humanize(this.props.pipeline.status)}
            />
            <DataListItem
              label={isLease ? 'Total Lease Value' : 'Sale Price'}
              value={formatCurrency(this.props.pipeline.salesPrice, 0)}
            />
            <DataListItem
              label='1031 Exchange'
              value={formatBoolean(this.props.pipeline.is1031Exchange)}
              visible={!isLease}
            />
            <DataListItem
              label='Listing'
              value={
                <Link
                  to={`/brokerage/listing/${this.props.pipeline.listing.id}`}
                  target='_blank'
                >
                  {this.props.pipeline.listing.title} (
                  {humanize(this.props.pipeline.listing.type)})
                </Link>
              }
            />
          </DataList>
        </div>
      </div>
    )
  }

  private _renderNotes() {
    return this.props.pipeline.notes
    ? (
      <div>
        <h5 className='section-title'>Notes</h5>
        <div className='mx-3 white-space-pre'>
          {this.props.pipeline.notes || 'None'}
        </div>
      </div>
    )
    : null
  }

  private _renderLeasePriceBreakdown(isLease: boolean) {
    if (!isLease) {
      return null
    }

    const summary = this.props.pipeline.leasePriceBreakdown.toArray().reduce(
      (result, term) => {
        result.totalPrice += term.totalPerTerm
        result.totalFees += term.fees
        return result
      },
      { totalPrice: 0, totalFees: 0 },
    )

    return (
      <div>
        <h5 className='section-title'>Lease Price Breakdown</h5>
        <div className='mx-3'>
          <div className='detail-card-group'>
            {this.props.pipeline.leasePriceBreakdown
              .toArray()
              .map((term, i) => (
                <div className='detail-card mb-2' key={`lease-term-${i}`}>
                  <LeaseTermDetailsCard term={term} />
                </div>
              ))}
          </div>
          <h6 className='section-title'>Totals</h6>
          <DataList>
            <DataListItem
              label='Price'
              value={formatCurrency(summary.totalPrice, 2)}
            />
            <DataListItem
              label='Fees'
              value={formatCurrency(summary.totalFees, 2)}
            />
          </DataList>
        </div>
      </div>
    )
  }

  private _renderSellerInformation(isLease: boolean) {
    return (
      <div>
        <h5 className='section-title'>
          {isLease ? 'Landlord Information' : 'Seller Information'}
        </h5>
        <div className='mx-3'>
          <DataList columnCount={2}>
            <DataListItem
              label={isLease ? 'Landlord(s)' : 'Seller(s)'}
              value={
                <PillListRenderer
                  list={this.props.pipeline.sellers}
                  baseNavigateUrl='/connection'
                  idValue='contactId'
                />
              }
            />
            <DataListItem
              label={isLease ? "Landlord's Agent(s)" : 'Listing Agent(s)'}
              value={
                this.props.pipeline.listingAgents.size === 0 ? (
                  'No Listing Agent'
                ) : (
                  <PillListRenderer
                    list={this.props.pipeline.listingAgents}
                    baseNavigateUrl='/connection'
                    idValue='contactId'
                  />
                )
              }
            />
            <DataListItem
              label={"Seller's Mortgage"}
              value={this.props.pipeline.sellerMortgage}
              visible={!isLease}
            />
            <DataListItem
              label={'Title Company'}
              value={
                this.props.pipeline.sellersTitleCompany
                  ? this.props.pipeline.sellersTitleCompany.name
                  : ''
              }
              visible={!isLease}
            />
          </DataList>
        </div>
      </div>
    )
  }
}
